/**
 * AI Chat React Query Hooks
 *
 * Individual React Query hooks for each AI Chat API operation.
 * These are the building blocks used by the combined useAIChat hook.
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import {
  ChatHistoryResponse,
  ActionConfirmationResponse,
} from '../types/ai-chat.types';
import {
  listConversations,
  createConversation,
  renameConversation,
  updateConversation,
  renameFolder,
  type ConversationUpdateFields,
  deleteConversation,
  sendMessage,
  getChatHistory,
  clearChatHistory,
  confirmAction,
  cancelAction,
} from './ai-chat-api';
import { AI_CHAT_QUERY_KEYS, DEFAULT_HISTORY_LIMIT } from './ai-chat-store';

// ─────────────────────────────────────────────────────────────────────────────
// Query Hooks (read operations)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get conversations list
 */
export function useChatHistoryList(limit = 20) {
  return useQuery({
    queryKey: [...AI_CHAT_QUERY_KEYS.conversations, limit],
    queryFn: () => listConversations(limit, 0),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: 1000,
  });
}

/**
 * Hook to get chat history for a specific conversation
 */
export function useChatHistory(conversationId?: string, initialLimit = DEFAULT_HISTORY_LIMIT) {
  return useQuery({
    queryKey: AI_CHAT_QUERY_KEYS.history(conversationId, initialLimit),
    queryFn: () => getChatHistory(initialLimit, 0, conversationId),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!conversationId,
  });
}

/**
 * Hook for infinite scroll chat history
 */
export function useInfiniteChatHistory(conversationId?: string, pageSize = DEFAULT_HISTORY_LIMIT) {
  return useInfiniteQuery({
    queryKey: [...AI_CHAT_QUERY_KEYS.history(conversationId, pageSize), 'infinite'],
    queryFn: ({ pageParam = 0 }) => getChatHistory(pageSize, pageParam, conversationId),
    getNextPageParam: (lastPage) => {
      const nextOffset = (lastPage.offset || 0) + (lastPage.limit || 50);
      return nextOffset < lastPage.total ? nextOffset : undefined;
    },
    initialPageParam: 0,
    staleTime: 0,
    refetchOnWindowFocus: false,
    enabled: !!conversationId,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Mutation Hooks (write operations)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to create a new conversation
 */
export function useCreateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createConversation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
    },
  });
}

/**
 * Hook to rename a conversation
 */
export function useRenameConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, title }: { conversationId: string; title: string }) =>
      renameConversation(conversationId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
    },
  });
}

/**
 * Hook to update a conversation (rename, move to folder, archive/unarchive)
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, fields }: { conversationId: string; fields: ConversationUpdateFields }) =>
      updateConversation(conversationId, fields),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
    },
  });
}

/**
 * Hook to rename a sidebar folder
 */
export function useRenameFolder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ from, to }: { from: string; to: string }) => renameFolder(from, to),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
    },
  });
}

/**
 * Hook to delete a conversation
 */
export function useDeleteConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: (_data, conversationId) => {
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
      // Verlauf der gelöschten Unterhaltung nicht im Speicher lassen.
      queryClient.removeQueries({ queryKey: ['ai-chat', 'history', conversationId] });
    },
  });
}

/**
 * Hook to send a message (non-streaming)
 */
export function useSendMessage() {
  return useMutation({
    mutationFn: sendMessage,
  });
}

/**
 * Hook to clear chat history
 */
export function useClearChatHistory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: clearChatHistory,
    onSuccess: (_data, conversationId) => {
      if (conversationId) {
        queryClient.setQueryData<ChatHistoryResponse>(
          AI_CHAT_QUERY_KEYS.history(conversationId, DEFAULT_HISTORY_LIMIT),
          { messages: [], total: 0, limit: DEFAULT_HISTORY_LIMIT, offset: 0 }
        );
        queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.history(conversationId, DEFAULT_HISTORY_LIMIT) });
      }
      queryClient.invalidateQueries({ queryKey: AI_CHAT_QUERY_KEYS.conversations });
    },
  });
}

/**
 * Hook to confirm an action
 */
export function useConfirmAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: confirmAction,
    onSuccess: (data: ActionConfirmationResponse) => {
      if (data.message) {
        queryClient.setQueryData<ChatHistoryResponse>(
          AI_CHAT_QUERY_KEYS.history(undefined, DEFAULT_HISTORY_LIMIT),
          (old: ChatHistoryResponse | undefined) => {
            if (!old) return { messages: [data.message!], total: 1, limit: DEFAULT_HISTORY_LIMIT, offset: 0 };
            return {
              ...old,
              messages: [...old.messages, data.message!],
              total: old.total + 1,
            };
          }
        );
      }
    },
  });
}

/**
 * Hook to cancel an action
 */
export function useCancelAction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelAction,
    onSuccess: (data: ActionConfirmationResponse) => {
      if (data.message) {
        queryClient.setQueryData<ChatHistoryResponse>(
          AI_CHAT_QUERY_KEYS.history(undefined, DEFAULT_HISTORY_LIMIT),
          (old: ChatHistoryResponse | undefined) => {
            if (!old) return { messages: [data.message!], total: 1, limit: DEFAULT_HISTORY_LIMIT, offset: 0 };
            return {
              ...old,
              messages: [...old.messages, data.message!],
              total: old.total + 1,
            };
          }
        );
      }
    },
  });
}
